import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';

/**
 * Query key root for the admin user typeahead. The active fragment is appended so distinct
 * fragments are cached (and re-fetched) independently.
 */
export const USER_AUTOCOMPLETE_QUERY_KEY = 'admin/user/autocomplete';

export interface IUseUserAutocompleteQueryOptions {
	/**
	 * The search text to match against users. The hook does NOT debounce — debouncing the
	 * fragment before passing it in is the CONSUMER's responsibility (e.g. wrap the input value
	 * in a `useDebounce`/`useDeferredValue` and feed the debounced value here). This keeps the
	 * query key stable between keystrokes the consumer wants to coalesce.
	 */
	fragment: string;
	/**
	 * Explicit override for whether the query runs. When omitted the query auto-enables for any
	 * non-empty (non-whitespace) fragment. Passing `false` force-disables regardless of fragment.
	 */
	enabled?: boolean;
	/** Max results to request. Mirrors the legacy ResourceAutoCompleteViewModel default of 5. */
	pageSize?: number;
	/**
	 * When `true`, guarantees the request is issued against the plain admin path
	 * (`user/autocomplete?...`) and is NEVER rewritten into an `impersonate/{accountId}/...`
	 * path. This hook is VM-free and calls `webServiceHelper.callAsync` with an explicit literal
	 * path, so no impersonation context is ever attached — the plain admin path is the only path
	 * it can produce. The flag exists to make that intent explicit at the call site; it has no
	 * alternate (impersonating) branch.
	 */
	preventImpersonation?: boolean;
}

/**
 * Builds the literal `user/autocomplete` request path with the `fragment` search param and
 * `pageSize`, replicating `ResourceAutoCompleteViewModel` (type `User`) without MobX. Exported for
 * testing/diagnostics.
 */
export const buildUserAutocompletePath = (fragment: string, pageSize: number): string => {
	// Encode with encodeURIComponent so spaces become `%20` (matching the legacy `query-string`
	// stringify the ResourceAutoCompleteViewModel used), rather than `URLSearchParams`' `+`.
	const fragmentParam = `fragment=${encodeURIComponent(fragment)}`;
	const pageSizeParam = `pageSize=${encodeURIComponent(String(pageSize))}`;
	return `user/autocomplete?${fragmentParam}&${pageSizeParam}`;
};

/**
 * VM-free typeahead query for users, modeled on the legacy `ResourceAutoCompleteViewModel`
 * (`type: User` -> `user/autocomplete`, search param `fragment`, default `pageSize` 5). Server
 * state only — no MobX, no view models.
 *
 * The call goes directly through `useUserSession().webServiceHelper.callAsync` with a literal
 * admin path, so it never routes through any impersonation rewrite (see `preventImpersonation`).
 *
 * Debounce the `fragment` in the consumer before passing it in; this hook intentionally does not.
 */
export function useUserAutocompleteQuery({
	fragment,
	enabled,
	pageSize = 5,
	// Destructured purely to document/accept the option at the call site. The plain admin path is
	// always used regardless, since no impersonation context is ever attached.
	preventImpersonation: _preventImpersonation,
}: IUseUserAutocompleteQueryOptions) {
	const userSession = useUserSession();

	const trimmedFragment = fragment.trim();
	const hasFragment = trimmedFragment.length > 0;

	return useQuery({
		queryKey: [USER_AUTOCOMPLETE_QUERY_KEY, trimmedFragment, pageSize],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IUser>>(
				buildUserAutocompletePath(trimmedFragment, pageSize),
				'GET'
			),
		enabled: hasFragment && enabled !== false,
	});
}
